import React from 'react';

import { postWebUiApi } from '../../api';
import { WebUiAlert } from '../../components/alert';
import { WebUiButton } from '../../components/button';
import { WebUiInput } from '../../components/input';
import WebUiPageWrapper from '../../components/page-wrapper';

export default function WebUiMaintenanceSettingsPage() {
  const [file, setFile] = React.useState<File | null>(null);
  const [cfgName, setCfgName] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [message, setMessage] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let alive = true;
    postWebUiApi<{ cfgName?: string }>({
      service: 'system',
      access: 'get',
      command: 'get config name',
    })
      .then((resp) => {
        if (!alive) return;
        const name = (resp.cfgName ?? '').replace(/\.cfg$/i, '');
        setCfgName(name);
      })
      .catch((err) => {
        if (!alive) return;
        setError((err as Error).message);
      });
    return () => {
      alive = false;
    };
  }, []);

  const run = React.useCallback(async (fn: () => Promise<void>, ok: string) => {
    try {
      setLoading(true);
      await fn();
      setMessage(ok);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <WebUiPageWrapper titleList={['Maintenance', 'Configuration']}>
      {message ? <WebUiAlert type="success" message={message} /> : null}
      {error ? <WebUiAlert type="error" message={error} /> : null}

      <div className="flex w-full flex-col gap-2">
        <h4 className="font-bold">Configuration</h4>
        <table className="webui-table max-w-fit">
          <thead>
            <tr className="webui-table-row-head">
              <th>Action</th>
              <th>Execute</th>
            </tr>
          </thead>
          <tbody>
            <tr className="webui-table-row-content">
              <td>Download</td>
              <td>
                <WebUiButton
                  disabled={loading}
                  onClick={() => {
                    run(async () => {
                      const cfg = await postWebUiApi<Record<string, unknown>>({
                        service: 'system',
                        access: 'set',
                        command: 'create config',
                      });
                      const blob = new Blob([JSON.stringify(cfg, null, 2)], {
                        type: 'application/json',
                      });
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `${cfgName || 'config'}.cfg`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      window.URL.revokeObjectURL(url);
                    }, 'Config downloaded.').catch(() => {});
                  }}
                >
                  Download Configuration
                </WebUiButton>
              </td>
            </tr>
            <tr className="webui-table-row-content">
              <td>Reset</td>
              <td>
                <WebUiButton
                  disabled={loading}
                  onClick={() => {
                    run(async () => {
                      await postWebUiApi({
                        service: 'system',
                        access: 'set',
                        command: 'factory reset',
                      });
                    }, 'Configuration reset requested.').catch(() => {});
                  }}
                >
                  Reset to Default Configuration
                </WebUiButton>
              </td>
            </tr>
            <tr className="webui-table-row-content">
              <td>Save ({cfgName || 'config'})</td>
              <td>
                <WebUiButton
                  disabled={loading}
                  onClick={() => {
                    run(async () => {
                      await postWebUiApi({
                        service: 'system',
                        access: 'set',
                        command: 'save config with name',
                        parameters: { cfgName: cfgName.trim().slice(0, 255) },
                      });
                    }, 'Config saved.').catch(() => {});
                  }}
                >
                  Save Changes
                </WebUiButton>
              </td>
            </tr>
            <tr className="webui-table-row-content">
              <td rowSpan={2}>Upload</td>
              <td>
                <WebUiInput
                  inputSize="sm"
                  type="file"
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
              </td>
            </tr>
            <tr className="webui-table-row-content">
              <td>
                <WebUiButton
                  disabled={loading || !file}
                  onClick={() => {
                    run(async () => {
                      if (!file) return;
                      const raw = await file.text();
                      const parsed = JSON.parse(raw) as Record<string, unknown>;
                      await postWebUiApi({
                        service: 'system',
                        access: 'set',
                        command: 'apply config',
                        parameters: parsed,
                      });
                    }, 'Config applied.').catch(() => {});
                  }}
                >
                  Upload &amp; Apply Configuration File
                </WebUiButton>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </WebUiPageWrapper>
  );
}
